import { Link } from "react-router-dom";
import type { Movie } from "@/lib/types";
import { formatDate, formatViews } from "@/lib/movies";

interface DetailHeroProps {
  movie: Movie;
}

export default function DetailHero({ movie }: DetailHeroProps) {
  const meta = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: `${movie.region} · ${movie.country}` },
    { label: "年份", value: String(movie.year) },
    { label: movie.type === "剧集" ? "集数" : "片长", value: movie.duration },
    { label: "语言", value: movie.language },
    { label: "更新", value: formatDate(movie.updatedAt) },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[230px_minmax(0,1fr)] gap-6 md:gap-10 items-start">
      <div className="relative w-[170px] sm:w-[210px] lg:w-full mx-auto lg:mx-0 aspect-[2/3] rounded-lg overflow-hidden border border-background-300/60 bg-background-200">
        <img
          src={movie.poster}
          alt={`${movie.title}影视海报`}
          title={`${movie.title}剧情介绍与分析`}
          className="w-full h-full object-cover object-top"
        />
        <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-background-50/85 backdrop-blur-sm text-accent-500 text-xs font-label inline-flex items-center gap-1">
          <i className="ri-star-fill text-[11px]"></i>
          {movie.rating.toFixed(1)}
        </span>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="px-2.5 py-1 rounded-md bg-primary-500 text-foreground-50 text-[11px] font-medium">
            {movie.type}
          </span>
          <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-300 text-[11px]">
            {movie.country}
          </span>
          <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-300 text-[11px]">
            {movie.year}
          </span>
          <span className="px-2.5 py-1 rounded-md bg-accent-500/15 text-accent-500 text-[11px]">
            {movie.genres[0]}
          </span>
        </div>

        <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-50 leading-tight">
          {movie.title}
        </h1>

        <p className="mt-3 text-sm md:text-base text-accent-500">{movie.tagline}</p>

        <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-400">
          {movie.summary}
        </p>

        <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-xs md:text-sm">
          {meta.map((item) => (
            <div key={item.label} className="flex gap-2 min-w-0">
              <dt className="text-foreground-600 shrink-0 w-10">{item.label}</dt>
              <dd className="text-foreground-300 truncate" title={item.value}>
                {item.value}
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-7 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
          <Link
            to="/category"
            className="h-10 px-5 inline-flex items-center justify-center gap-2 rounded-md border border-background-300 text-foreground-200 text-sm hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回分类
          </Link>
          <a
            href="#plot"
            className="h-10 px-5 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-foreground-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-book-open-line"></i>
            阅读剧情解析
          </a>
          {movie.episodes && (
            <a
              href="#episodes"
              className="h-10 px-5 inline-flex items-center justify-center gap-2 rounded-md border border-background-300 text-foreground-200 text-sm hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-list-2-line"></i>
              分集剧情
            </a>
          )}
          <span className="sm:ml-auto inline-flex items-center gap-1.5 text-xs text-foreground-600">
            <i className="ri-eye-line"></i>
            {formatViews(movie.views)} 次阅读
          </span>
        </div>
      </div>
    </div>
  );
}